/**
 * useAlertHighlight
 *
 * Hook para resaltar (con animación pulsante ámbar) las filas que coinciden con
 * un parámetro `?highlight=...` en la URL — usado por las alertas inteligentes
 * del dashboard cuando llevan al user a CxC/CxP. La idea es que el user vea de
 * inmediato cuáles filas son las referenciadas, sin tener que filtrar a mano.
 *
 * Uso:
 *   const { isHighlighted, highlightedRef } = useAlertHighlight({
 *     items: facturas,
 *     getId: f => f.id,
 *     matcher: {
 *       vencidas: f => f.fechaVencimiento && new Date(f.fechaVencimiento) < new Date() && f.estadoPago !== 'pagado',
 *       por_vencer_7d: f => { ... }
 *     }
 *   });
 *
 *   // En el <tr>:
 *   ref={isHighlighted(f.id) ? highlightedRef : null}
 *   className={clsx('...', isHighlighted(f.id) && 'animate-row-highlight relative z-[1]')}
 *
 * Comportamiento:
 *   - Lee `?highlight=<key>` de la URL una sola vez al cargar.
 *   - Calcula los IDs que matchean cuando `items` cambia (no antes).
 *   - Auto-scroll al primer match.
 *   - Apaga la animación tras 12s para que no quede molesta.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const ANIMATION_MS = 12000;

export default function useAlertHighlight({ items, getId, matcher }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightKey = searchParams.get('highlight');
  const [animOn, setAnimOn] = useState(true);
  const firstScrolledRef = useRef(false);
  const highlightedRef = useRef(null);

  // IDs que matchean — se recalcula cuando cambian items o highlightKey
  const matchedIds = useMemo(() => {
    if (!highlightKey || !matcher?.[highlightKey] || !Array.isArray(items)) return new Set();
    const fn = matcher[highlightKey];
    return new Set(items.filter(it => { try { return fn(it); } catch { return false; } }).map(getId));
  }, [items, highlightKey, matcher, getId]);

  // Auto-scroll al primer match — una sola vez por entrada
  useEffect(() => {
    if (highlightKey && matchedIds.size > 0 && highlightedRef.current && !firstScrolledRef.current) {
      firstScrolledRef.current = true;
      setTimeout(() => {
        highlightedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, [highlightKey, matchedIds]);

  // Auto-apagar la animación tras 12s; el resaltado de IDs se conserva por si
  // el user quiere seguir viéndolas (fondo sutil sin pulso).
  useEffect(() => {
    if (!highlightKey || matchedIds.size === 0) return;
    const t = setTimeout(() => setAnimOn(false), ANIMATION_MS);
    return () => clearTimeout(t);
  }, [highlightKey, matchedIds]);

  const clearHighlight = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('highlight');
    setSearchParams(next, { replace: true });
  };

  return {
    highlightKey,
    matchedIds,
    isHighlighted: (id) => matchedIds.has(id),
    animOn,                // false tras 12s — usar para condicional de animate-row-highlight
    highlightedRef,        // ref al primer match para scroll
    clearHighlight,
    hasMatches: matchedIds.size > 0
  };
}
